import { type IMatchConfigEntry, type IProceduralMapTfConfig } from '@nova/types';
import { type StubTiledMapOptions } from '@nova/utils';

/**
 * MAP geometry / presentation recipes for procedural Terraforming maps.
 * Keyed by lobby match config {@link IMatchConfigEntry.id}. Match rules stay on `gamePlayConfig`.
 * Tile pixel size is the shared action-game `pxPerUnit`, not authored per map.
 */

/**
 * Classic left LZ + right stacked stations inside an 8×8 camp.
 * Omit origin for left-wall band placement; set origin `{ x, y }` to pin the map-tile top-left.
 */
const DEFAULT_TF_BASECAMP = {
  width: 8,
  height: 8,
  grassStation: { x: 4, y: 0, width: 4, height: 4 },
  flowerStation: { x: 4, y: 4, width: 4, height: 4 },
  /** ~2×2 tiles at 150px pitch; centered in each 4×4 land stamp when nav/collision emit. */
  stationEntitySize: { width: 200, height: 200 }
} as const;

const DEFAULT_TF_SEED_BURST_PADS = {
  size: { width: 4, height: 4 },
  spawnBufferTiles: 2
} as const;

export const proceduralMapTfById = {
  'tf-coop-25': {
    id: 'tf-coop-25',
    widthTiles: 50,
    heightTiles: 50,
    fillLayoutId: 'singleRightAnglesAndLines',
    obstacleTileMaxCoverage: 0.3,
    minimumRouteWidthTiles: 2,
    minimumPlacementSizeTiles: 3,
    maximumPlacementSizeTiles: 16,
    basecamp: DEFAULT_TF_BASECAMP,
    seedBurstPads: DEFAULT_TF_SEED_BURST_PADS,
    presentation: {
      groundTilesetKey: 'tf-ground-open',
      grassTilesetKey: 'tf-terrain-grass',
      obstacleTilesetKey: 'tf-obstacle'
    }
  },
  'tf-coop-compact': {
    id: 'tf-coop-compact',
    widthTiles: 24,
    heightTiles: 20,
    fillLayoutId: 'singleRightAnglesAndLines',
    obstacleTileMaxCoverage: 0.22,
    minimumRouteWidthTiles: 2,
    minimumPlacementSizeTiles: 2,
    maximumPlacementSizeTiles: 12,
    basecamp: DEFAULT_TF_BASECAMP,
    seedBurstPads: DEFAULT_TF_SEED_BURST_PADS,
    presentation: {
      groundTilesetKey: 'tf-ground-open-compact',
      grassTilesetKey: 'tf-terrain-grass-compact',
      obstacleTilesetKey: 'tf-obstacle-compact'
    }
  }
} as const satisfies Record<string, IProceduralMapTfConfig>;

export type ProceduralMapTfId = keyof typeof proceduralMapTfById;

function isProceduralMapTfId(mapId: string): mapId is ProceduralMapTfId {
  return Object.prototype.hasOwnProperty.call(proceduralMapTfById, mapId);
}

export function getProceduralMapTfConfig(mapId: string): IProceduralMapTfConfig | undefined {
  return isProceduralMapTfId(mapId) ? proceduralMapTfById[mapId] : undefined;
}

export interface ProceduralStubResolveOptions {
  /** Shared action-game tile pitch (`pxPerUnit`). When omitted, the stub builder default applies. */
  tilePx?: number;
}

/** Stub builder options derived from a procedural MAP recipe (presentation grid, incl. border ring). */
export function proceduralMapTfToStubOptions(
  config: IProceduralMapTfConfig,
  options?: ProceduralStubResolveOptions
): StubTiledMapOptions {
  const borderOutset = Math.max(0, Math.floor(config.presentation.borderOutsetTiles ?? 1));
  return {
    widthTiles: config.widthTiles + (2 * borderOutset),
    heightTiles: config.heightTiles + (2 * borderOutset),
    tilePx: options?.tilePx
  };
}
